// Libraries
import React, { useState, useCallback, useLayoutEffect } from 'react'
import { Modal, Typography, Button, Paper, Box } from '@mui/material'
import { useSelector, shallowEqual } from 'react-redux'
import { ToastContainer, toast, ToastOptions, Slide } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Types and Interfaces
import type { RootState } from '../../store/store'
import { Feedback } from '../../store/slices/function/feedback'

// Styles
import '../../assets/toast-custom.css'

//type Status = 'Success' | 'Error' | 'Warning' | 'Information';
const toastOptions: ToastOptions = {
	position: 'top-center',
	hideProgressBar: false,
	closeOnClick: false,
	toastId: 1212,
	transition: Slide,
	autoClose: false,
	theme: 'colored',
	style: {
		width: '500px',
	},
}

export default function Alerts() {
	const feedback = useSelector((state: RootState) => state.feedBackSlice, shallowEqual)
	const [detailsModalOpen, setDetailsModalOpen] = useState<boolean>(false)
	const [feedbackDetails, setFeedbackDetails] = useState<Feedback | null>(null)
	const [feedbacks, setFeedbacks] = useState<Feedback[]>([])

	/**
	 * Handles the closing of a feedback toast notification.
	 * This function is called when a toast notification is closed, either automatically after a timeout or manually by the user.
	 * It removes the corresponding feedback from the local state, which in turn removes the toast from the UI.
	 * The function does not return a value, but it updates the local state to ensure that the UI remains in sync with the current feedback messages being displayed to the user.
	 * This function is essential for managing the lifecycle of feedback messages in the application, allowing users to dismiss notifications and ensuring that the UI accurately reflects the current state of feedback without cluttering the interface with outdated messages.
	 * @param feedback - The feedback object associated with the toast that is being closed. This object contains information about the feedback message, status, and any additional details that may be relevant for the user.
	 * @see toast for displaying feedback messages as toast notifications in the UI
	 */
	const handleClose = (feedback: Feedback) => {
		const newFeedbacks = feedbacks.filter(f => f.id !== feedback.id)
		setFeedbacks(newFeedbacks)
	}

	/**
	 * Handles the opening of the feedback details modal.
	 * This function is called when the user clicks the "View Details" button on a feedback toast notification that has additional details available.
	 * It sets the local state to open the details modal and populates it with the relevant feedback details, allowing the user to view more in-depth information about the feedback message, such as error codes, exception messages, stack traces, and any other relevant data that can help them understand the context of the feedback and take appropriate action if necessary.
	 * The function does not return a value, but it updates the local state to control the visibility of the details modal and the content that is displayed within it, providing a seamless user experience for accessing detailed feedback information directly from the toast notifications.
	 * @param feedback - The feedback object containing the details to be displayed in the modal. This object includes information such as error codes, messages, exception details, and any other relevant data that can help the user understand the context of the feedback and take appropriate action if necessary.
	 * @see Modal for displaying the feedback details in a modal dialog
	 * @see toast for displaying feedback messages as toast notifications in the UI, which can trigger the opening of the details modal when the user interacts with them
	 * @see handleClose for managing the lifecycle of feedback messages and ensuring that the UI remains in sync with the current state of feedback being displayed to the user
	 * @see hasDetailedInfo for determining whether a feedback message has additional details that can be viewed in the modal, which can influence whether the "View Details" button is displayed on the toast notification
	 * @see renderField and renderExceptionData for rendering the various fields and details within the feedback details modal, providing a structured and informative display of the feedback information for the user to review.
	 */
	const openDetailsModal = useCallback((feedback: Feedback) => {
		setDetailsModalOpen(true)
		setFeedbackDetails(feedback)
	}, [])

	/**
	 * Handles the closing of the feedback details modal.
	 * This function is called when the user clicks the close button on the feedback details modal or clicks outside the modal to dismiss it.
	 * It sets the local state to close the details modal and clears any feedback details that were previously set, ensuring that the modal is reset and ready for the next time it is opened with new feedback information.
	 * The function does not return a value, but it updates the local state to control the visibility of the details modal and the content that is displayed within it, providing a seamless user experience for accessing and dismissing detailed feedback information directly from the toast notifications.
	 * @see Modal for displaying the feedback details in a modal dialog, which can be closed using this function
	 * @see openDetailsModal for managing the opening of the details modal and populating it with relevant feedback information when the user interacts with toast notifications
	 * @see handleClose for managing the lifecycle of feedback messages and ensuring that the UI remains in sync with the current state of feedback being displayed to the user, which can influence when the details modal is opened or closed based on user interactions with toast notifications
	 * @see hasDetailedInfo for determining whether a feedback message has additional details that can be viewed in the modal, which can influence whether the "View Details" button is displayed on the toast notification and subsequently trigger the opening of the details modal when clicked by the user
	 * @see renderField and renderExceptionData for rendering the various fields and details within the feedback details modal, providing a structured and informative display of the feedback information for the user to review, which can be reset when the modal is closed using this function to ensure that old feedback details are not displayed the next time the modal is opened with new feedback information.
	 * @see handleRetryFailedApprovals for managing the retry process of failed timesheet approvals, which can involve opening the details modal to display error information and allowing the user to take action based on the feedback provided in the modal.
	 * @see useSignalR for managing the SignalR connection used for real-time updates during the retry process of failed timesheet approvals, which can also involve displaying feedback messages and details in the modal based on the outcomes of the retry attempts.
	 * @see useApproveTimesheetBatchMutation for the mutation used to trigger the approval of timesheets during the retry process, which can also involve displaying feedback messages and details in the modal based on the outcomes of the retry attempts and any errors that may occur during the process.
	 * @see resetProgress and setData for managing the progress state in the Redux store during the retry process of failed timesheet approvals, which can also involve displaying feedback messages and details in the modal based on the progress and outcomes of the retry attempts.
	 * @see timesheetApi.util.invalidateTags for revalidating the timesheet totals after the retry process is complete, which can also involve displaying feedback messages and details in the modal based on the outcomes of the retry attempts and any errors that may occur during the process.
	 */
	const closeDetailsModal = useCallback(() => {
		setDetailsModalOpen(false)
		setFeedbackDetails(null)
	}, [])

	/**
	 * Determines whether a feedback message has additional details that can be viewed in the modal.
	 * This function checks the feedback object for the presence of various fields that would indicate that there are detailed information available for the user to view, such as exception messages, stack traces, inner exception messages, exception sources, or any additional data related to the exception.
	 * It returns a boolean value indicating whether detailed information is available, which can be used to conditionally render the "View Details" button on the toast notification and allow the user to access the details modal when clicked.
	 * The function does not perform any side effects or state updates, but it provides a utility for determining the availability of detailed feedback information based on the content of the feedback object, enhancing the user experience by only offering the option to view details when there is actually information to be displayed in the modal.
	 * @param feedback - The feedback object to be evaluated for the presence of detailed information. This object may contain various fields such as exceptionMessage, stackTrace, innerExceptionMessage, exceptionSource, and exceptionData, which can indicate that there are details available for the user to view in the modal.
	 * @return A boolean value indicating whether the feedback message has additional details that can be viewed in the modal. Returns true if any of the relevant fields are present in the feedback object, and false if none of those fields are present, indicating that there are no details available for viewing in the modal.
	 * @see openDetailsModal for managing the opening of the details modal and populating it with relevant feedback information when the user interacts with toast notifications, which can be influenced by the presence of detailed information as determined by this function.
	 * @see handleClose for managing the lifecycle of feedback messages and ensuring that the UI remains in sync with the current state of feedback being displayed to the user, which can influence when the details modal is opened or closed based on user interactions with toast notifications and the availability of detailed information as determined by this function.
	 * @see renderField and renderExceptionData for rendering the various fields and details within the feedback details modal, providing a structured and informative display of the feedback information for the user to review, which can be influenced by the presence of detailed information as determined by this function.
	 * @see handleRetryFailedApprovals for managing the retry process of failed timesheet approvals, which can involve opening the details modal to display error information and allowing the user to take action based on the feedback provided in the modal, which can be influenced by the presence of detailed information as determined by this function.
	 * @see useSignalR for managing the SignalR connection used for real-time updates during the retry process of failed timesheet approvals, which can also involve displaying feedback messages and details in the modal based on the outcomes of the retry attempts, which can be influenced by the presence of detailed information as determined by this function.
	 * @see useApproveTimesheetBatchMutation for the mutation used to trigger the approval of timesheets during the retry process, which can also involve displaying feedback messages and details in the modal based on the outcomes of the retry attempts and any errors that may occur during the process, which can be influenced by the presence of detailed information as determined by this function.
	 * @see resetProgress and setData for managing the progress state in the Redux store during the retry process of failed timesheet approvals, which can also involve displaying feedback messages and details in the modal based on the progress and outcomes of the retry attempts, which can be influenced by the presence of detailed information as determined by this function.
	 * @see timesheetApi.util.invalidateTags for revalidating the timesheet totals after the retry process is complete, which can also involve displaying feedback messages and details in the modal based on the outcomes of the retry attempts and any errors that may occur during the process, which can be influenced by the presence of detailed information as determined by this function.
	 */
	const hasDetailedInfo = (feedback: Feedback | null) => {
		if (!feedback) return false
		return !!(
			feedback.exceptionMessage ||
			feedback.stackTrace ||
			feedback.innerExceptionMessage ||
			feedback.exceptionSource ||
			feedback.exceptionData
		)
	}

	interface ToastContentWrapperProps {
		feedback: Feedback
		closeToast?: () => void
		toastProps?: any
		isPaused?: boolean
	}

	/**
	 * Component for rendering the content of a toast notification for feedback messages.
	 * This component is designed to display the feedback message, status, and any relevant details in a structured and visually appealing way within the toast notification. It also includes buttons for copying error codes and viewing detailed information in a modal when applicable.
	 * The component is memoized using React.forwardRef to optimize performance and prevent unnecessary re-renders, ensuring that it only updates when the relevant feedback data changes. It also filters out any props that are specific to the react-toastify library to avoid passing them down to the underlying Box component, which is used for layout and styling of the toast content.
	 * The component does not manage any state or side effects on its own, but it relies on the props passed to it to determine what content to display and how to handle user interactions such as copying error codes or opening the details modal. It provides a reusable and customizable way to render feedback messages in toast notifications across the application, enhancing the user experience by providing clear and actionable feedback in response to various operations and events.
	 * @param feedback - The feedback object containing the message, status, and any relevant details to be displayed in the toast notification. This object may include fields such as errorCode, message, status, environment, and any other relevant information that can be used to render the content of the toast and provide context to the user.
	 * @param closeToast - An optional function that can be called to programmatically close the toast notification. This can be used in conjunction with user interactions within the toast content, such as clicking a button to view details or copy an error code, allowing for a seamless user experience when interacting with feedback messages in toast notifications.
	 * @param toastProps - Any additional props passed by the react-toastify library when rendering the toast notification. These props are filtered out and not passed down to the underlying Box component to avoid unintended side effects or styling issues, ensuring that only relevant props related to the feedback content are used for rendering.
	 * @param isPaused - An optional boolean indicating whether the toast notification is currently paused (e.g., when the user hovers over it). This can be used to conditionally render certain elements or adjust the behavior of the toast content based on whether the toast is active or paused, enhancing the interactivity and responsiveness of the feedback messages displayed in toast notifications.
	 * @returns A JSX element representing the content of the toast notification for the given feedback message, including the main message, status, and any relevant details, as well as buttons for copying error codes and viewing detailed information in a modal when applicable.
	 * @see handleClose for managing the lifecycle of feedback messages and ensuring that the UI remains in sync with the current state of feedback being displayed to the user, which can influence when the details modal is opened or closed based on user interactions with toast notifications and the availability of detailed information as determined by this component.
	 * @see openDetailsModal for managing the opening of the details modal and populating it with relevant feedback information when the user interacts with toast notifications, which can be triggered by buttons rendered within this component based on the presence of detailed information in the feedback object.
	 * @see hasDetailedInfo for determining whether a feedback message has additional details that can be viewed in the modal, which can influence whether certain buttons are rendered within
	 */
	const ToastContentWrapper = React.forwardRef<HTMLDivElement, ToastContentWrapperProps>(
		({ feedback, closeToast, toastProps, isPaused, ...rest }, ref) => {
			// Filter react-toastify props - they won't be passed to the Box element
			// closeToast, toastProps, isPaused are extracted and not used in the DOM

			return (
				<Box
					ref={ref}
					{...rest}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
						width: '100%',
					}}
				>
					{/* Top bar with Error Code, Copy button and View Details button */}
					{feedback.errorCode && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%',
							}}
						>
							<Typography
								variant='subtitle2'
								sx={{ color: 'white', fontSize: '0.875rem', fontFamily: 'var(--font-family)' }}
							>
								Error Code: <b>{feedback.errorCode}</b>
							</Typography>
							<Box sx={{ display: 'flex', gap: '8px' }}>
								<Button
									size='small'
									variant='text'
									sx={{
										minWidth: 'auto',
										color: 'white',
										padding: '2px 8px',
										fontSize: '0.75rem',
										borderRadius: '4px',
										'&:hover': {
											color: 'black',
										},
									}}
									onClick={() => {
										if (feedback.errorCode) {
											navigator.clipboard.writeText(feedback.errorCode)
										}
									}}
									title='Copy Code'
								>
									Copy
								</Button>

								{/* View Details button */}
								{hasDetailedInfo(feedback) &&
									(feedback.environment?.toUpperCase() === 'DEVELOPMENT' ||
										feedback.environment?.toUpperCase() === 'QUALITY') && (
										<Button
											title='View Details'
											size='small'
											onClick={() => openDetailsModal(feedback)}
											sx={{
												fontSize: '0.75rem',
												backgroundColor: '#D32F2F',
												color: 'white',
												padding: '2px 8px',
												borderRadius: '4px',
												textTransform: 'none',
												'&:hover': {
													color: 'black',
												},
												marginRight: '10px',
											}}
										>
											View Details
										</Button>
									)}
							</Box>
						</Box>
					)}

					{/* Main message */}
					{feedback.message && (
						<Box
							sx={{
								color: 'white',
								fontSize: '0.875rem',
								lineHeight: '1.4',
							}}
						>
							<Typography
								variant='subtitle2'
								sx={{ color: 'white', fontSize: '0.875rem', fontFamily: 'var(--font-family)' }}
							>
								{feedback.message}
							</Typography>
						</Box>
					)}
				</Box>
			)
		},
	)

	// Set display name for debugging purposes
	ToastContentWrapper.displayName = 'ToastContentWrapper'

	// Function to generate the content for the toast notification based on the feedback object
	const toastContent = (feedback: Feedback) => {
		return <ToastContentWrapper feedback={feedback} />
	}

	/**
	 * Generates the options for the toast notification based on the feedback object.
	 * @param feedback - The feedback object containing information about the toast notification.
	 * @returns An object containing the options for the toast notification, including autoClose duration, onClose handler, and toastId.
	 */
	const getToastOptions = (feedback: Feedback): ToastOptions => {
		return {
			...toastOptions,
			autoClose: feedback.status === 'Success' ? 5000 : 10000,
			onClose: () => handleClose(feedback),
			toastId: feedback.id?.toString() || '',
		}
	}

	/**
	 * Effect to handle changes in the feedback state and trigger toast notifications accordingly.
	 * This effect listens for changes in the feedback object from the Redux store and checks if there is a new feedback message to display. If a new message is present, it creates a new feedback object with a unique ID, updates the local state to include this new feedback, and calls the function to display a toast notification with the appropriate content and options based on the feedback's status.
	 * The effect ensures that users receive timely and relevant feedback messages in response to various operations and events within the application, enhancing the user experience by providing clear and actionable information through toast notifications. It also manages the local state of feedback messages to keep track of which notifications are currently being displayed and allows for proper handling of toast lifecycle events such as closing notifications when they are dismissed by the user or automatically after a certain duration.
	 * @see toast for displaying feedback messages as toast notifications in the UI, which is triggered by this effect when new feedback messages are detected in the Redux store.
	 * @see handleClose for managing the lifecycle of feedback messages and ensuring that the UI remains in sync with the current state of feedback being displayed to the user, which can influence when the details modal is opened or closed based on user interactions with toast notifications and the availability of detailed information as determined by this effect.
	 * @see openDetailsModal for managing the opening of the details modal and populating it with relevant feedback information when the user interacts with toast notifications, which can be triggered by buttons rendered within the toast content based on the presence of detailed information in the feedback object, which is also influenced by this effect when new feedback messages are detected.
	 * @see hasDetailedInfo for determining whether a feedback message has additional details that can be viewed in the modal, which can influence whether certain buttons are rendered within the toast
	 */
	useLayoutEffect(() => {
		if (feedback.message && feedback.message !== '') {
			const newFeedback = {
				...feedback,
				id: Math.random().toString(36).substring(7),
			}
			setFeedbacks([...feedbacks, newFeedback])
			callToast(newFeedback)
		}
	}, [feedback])

	/**
	 * Displays a toast notification based on the feedback object's status.
	 * This function checks the status of the feedback and calls the appropriate toast method (success, error, warning, info) to display the notification with the content generated by the toastContent function and options generated by the getToastOptions function. It also handles a specific case for 'PartialContent' status where it does not show a toast notification, as that status is used for a specific feature that has its own custom snackbar for better user experience.
	 * @param feedback - The feedback object containing information about the toast notification.
	 * @see toast for displaying feedback messages as toast notifications in the UI, which is triggered by this function based on the status of the feedback object.
	 * @see toastContent for generating the content of the toast notification based on the feedback object, which is used when calling the toast methods to display the notification.
	 * @see getToastOptions for generating the options for the toast notification based on the feedback object, which is used when calling the toast methods to display the notification with the appropriate settings such as autoClose duration and onClose handler.
	 * @see useLayoutEffect for managing changes in the feedback state and triggering this function to display toast notifications accordingly when new feedback messages are detected in the Redux store.
	 * @see handleClose for managing the lifecycle of feedback messages and ensuring that the UI remains in sync with the current state of feedback being displayed to the user, which can influence when this function is called to display or close toast notifications based on user interactions and feedback updates
	 */
	const callToast = (feedback: Feedback) => {
		switch (feedback.status?.toUpperCase()) {
			// 'PartialContent' -> response from the mass approval endpoint. Don't show a toast for this status, as the feature has its own custom snackbar that better explains the situation to the user
			case 'PARTIALCONTENT':
				break
			case 'SUCCESS':
				toast.success(toastContent(feedback), getToastOptions(feedback))
				break
			case 'ERROR':
				toast.error(toastContent(feedback), getToastOptions(feedback))
				break
			case 'WARNING':
				toast.warning(toastContent(feedback), getToastOptions(feedback))
				break
			case 'INFORMATION':
			default:
				toast.info(toastContent(feedback), getToastOptions(feedback))
				break
		}
	}

	/**
	 * Renders a header field with a label and value.
	 * @param label - The label for the header field.
	 * @param value - The value for the header field.
	 * @returns A JSX element representing the header field, or null if the value is not provided.
	 */
	const renderHeaderField = (label: string, value: string | null | undefined) => {
		if (!value) return null

		return (
			<Box
				mb={2}
				sx={{ display: 'flex', alignItems: 'center' }}
			>
				<Typography
					fontFamily='Poppins'
					variant='subtitle1'
					fontWeight='600'
					fontStyle='SemiBold'
					fontSize='14px'
					lineHeight='157%'
					letterSpacing='0px'
					sx={{
						color: 'var(--primary-light)',
					}}
				>
					{label}:&nbsp;
				</Typography>
				<Typography
					fontFamily='Poppins'
					variant='subtitle1'
					fontWeight='600'
					fontStyle='SemiBold'
					fontSize='14px'
					lineHeight='157%'
					letterSpacing='0px'
					sx={{
						color: 'var(--primary-dark)',
					}}
				>
					{value}
				</Typography>
			</Box>
		)
	}

	/**
	 * Renders a field with a label and value, with optional styling for code or subtitle.
	 * @param label - The label for the field.
	 * @param value - The value for the field.
	 * @param isCode - Whether the field should be styled as code.
	 * @param isSubtitle - Whether the field should be styled as a subtitle.
	 * @returns A JSX element representing the field, or null if the value is not provided.
	 */
	const renderField = (
		label: string,
		value: string | null | undefined,
		isCode: boolean = false,
		isSubtitle: boolean = false,
	) => {
		if (!value) return null

		return (
			<Box mb={2}>
				<Typography
					variant={isSubtitle ? 'subtitle2' : 'subtitle1'}
					sx={{
						color: 'var(--primary-dark)',
						fontFamily: 'Poppins',
						fontWeight: '400',
						fontStyle: 'Regular',
						fontSize: '14px',
						lineHeight: '12px',
						letterSpacing: '0.15px',
						marginBottom: '10px',
						marginTop: '10px',
					}}
				>
					{label}:
				</Typography>
				<Typography
					variant='body1'
					sx={
						isCode
							? {
									wordBreak: 'break-word',
									whiteSpace: isCode && label === 'Stack Trace' ? 'pre-wrap' : 'normal',
									fontFamily: 'Poppins',
									backgroundColor: 'var(--secondary-light)',
									padding: '8px',
									borderRadius: '4px',
									fontWeight: '500',
									lineHeight: '130%',
									fontSize: '13px',
								}
							: {}
					}
				>
					{value}
				</Typography>
			</Box>
		)
	}

	/**
	 * Renders the exception data from the feedback object.
	 * @param feedback - The feedback object containing exception data.
	 * @returns A JSX element representing the exception data, or null if no exception data is available.
	 */
	const renderExceptionData = (feedback: Feedback) => {
		if (
			!feedback.exceptionData ||
			(typeof feedback.exceptionData === 'object' && Object.keys(feedback.exceptionData).length === 0)
		) {
			return null
		}

		return (
			<Box mb={2}>
				<Typography
					variant='subtitle1'
					fontWeight='bold'
					sx={{
						color: 'var(--primary-dark)',
						fontFamily: 'Poppins',
					}}
				>
					Exception Data:
				</Typography>
				<Box
					sx={{
						backgroundColor: '#f5f5f5',
						padding: '8px',
						borderRadius: '4px',
						fontFamily: 'monospace',
						maxHeight: '300px',
						overflow: 'auto',
					}}
				>
					{Object.keys(feedback.exceptionData).map(key => (
						<Box key={key}>
							<Typography
								component='span'
								fontWeight='bold'
								sx={{ fontFamily: 'var(--font-family)' }}
							>
								{key}:
							</Typography>{' '}
							{typeof feedback.exceptionData![key] === 'object'
								? JSON.stringify(feedback.exceptionData![key], null, 2)
								: String(feedback.exceptionData![key])}
						</Box>
					))}
				</Box>
			</Box>
		)
	}

	return (
		<>
			<Box>
				<ToastContainer
					position='top-right'
					hideProgressBar={true}
					newestOnTop
					closeOnClick
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme='colored'
				/>

				<Modal
					open={detailsModalOpen}
					onClose={() => closeDetailsModal()}
					aria-labelledby='error-details-modal'
					aria-describedby='detailed-error-information'
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Paper
						elevation={3}
						sx={{
							width: '80%',
							maxWidth: '800px',
							maxHeight: '80vh',
							padding: '24px',
							overflowY: 'auto',
							position: 'relative',
						}}
						onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
					>
						<Button
							sx={{
								position: 'absolute',
								top: '10px',
								right: '10px',
								minWidth: '30px',
								width: '30px',
								height: '30px',
								padding: '0',
								borderRadius: '50%',
								color: 'rgba(0, 0, 0, 0.6)',
							}}
							onClick={() => closeDetailsModal()}
						>
							✕
						</Button>

						<Typography
							variant='h1'
							component='div'
							gutterBottom
							color='var(--primary-dark)'
							sx={{
								fontFamily: 'Poppins',
								fontWeight: '600',
								fontStyle: 'SemiBold',
								fontSize: '16px',
								lineHeight: '175%',
								letterSpacing: '0.15px',
								marginBottom: '20px',
							}}
						>
							Error Details
						</Typography>

						{renderHeaderField('Error Code', feedbackDetails?.errorCode)}
						{renderHeaderField('Message', feedbackDetails?.message)}
						{renderField('Exception Message', feedbackDetails?.exceptionMessage, true)}
						{renderField('Exception Source', feedbackDetails?.exceptionSource, true)}
						{renderField('Stack Trace', feedbackDetails?.stackTrace, true)}
						{renderField('Inner Exception Message', feedbackDetails?.innerExceptionMessage, true, true)}
						{feedbackDetails && renderExceptionData(feedbackDetails)}

						<Box
							mt={2}
							display='flex'
							justifyContent='flex-end'
						>
							<Button
								variant='text'
								color='primary'
								sx={{
									fontFamily: 'Poppins',
									fontWeight: '600',
									fontStyle: 'SemiBold',
									fontSize: '14px',
									lineHeight: '24px',
									letterSpacing: '0px',
								}}
								onClick={() => closeDetailsModal()}
							>
								Fechar
							</Button>
						</Box>
					</Paper>
				</Modal>
			</Box>
		</>
	)
}
